import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Button } from "@blueprintjs/core";
import {
  faCheckCircle,
  faQuestionCircle,
  faUserShield,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Sticker } from "@components/Sticker";
import {
  GITHUB_WORKFLOW_FILE_PATH,
  SEMGREP_IGNORE_FILE_PATH,
} from "@constants";

import { AuthStep } from "@pages/login/types";

type Content = {
  [step in AuthStep]: {
    title: string;
    description: React.ReactNode;
  }[];
};

const PERMISSIONS: Content = {
  login: [
    {
      title: "Reading your email addresses",
      description: "So that you can receive operational notices.",
    },
  ],
  install: [
    {
      title: "Reading metadata of the repositories you select",
      description: "Lets Semgrep list their names on the project setup page.",
    },
    {
      title: "Reading the list of organization members",
      description:
        "Lets Semgrep determine who can manage your Semgrep organization based on your GitHub organization's members list.",
    },
    {
      title: "Writing (and reading) pull requests",
      description: "Lets Semgrep comment about findings on pull requests",
    },
    {
      title: "Writing (and reading) actions",
      description:
        "Allows Semgrep App to cancel stuck jobs, rerun jobs, pull logs from jobs, and perform on-demand scanning",
    },
    {
      title: "Reading checks",
      description:
        "Facilitates debugging of Semgrep App when configured out of GitHub Actions",
    },
    {
      title: "Writing (and reading) security events",
      description:
        "Enables integration with GitHub Advanced Security (for example, to show Semgrep results)",
    },
    {
      title: "Writing (and reading) secrets",
      description:
        "Enables automatically adding of the Semgrep App Token to your repository secrets when onboarding projects. Note: We cannot read the values of your existing or future secrets (only the names).",
    },
    {
      title: "Writing (and reading) 2 files",
      description: (
        <span>
          Lets Semgrep configure itself to run in CI by writing to{" "}
          <code>{GITHUB_WORKFLOW_FILE_PATH}</code> and{" "}
          <code>{SEMGREP_IGNORE_FILE_PATH}</code>.
        </span>
      ),
    },
    {
      title: "Writing (and reading) workflows",
      description: (
        <span>
          Lets Semgrep configure itself to run in CI by writing to{" "}
          <code>{GITHUB_WORKFLOW_FILE_PATH}</code>. GitHub allows writing to
          files within <code>.github/workflows/</code> only if this permission
          is granted along with "Writing a single file".
        </span>
      ),
    },
  ],
};

const FEATURES: Content = {
  login: [
    {
      title: "Save named rules",
      description:
        "Keep an organized list of rules you’ve written in the playground.",
    },
    {
      title: "Add GitHub organizations",
      description:
        "To manage policies and track Semgrep findings across repositories.",
    },
  ],
  install: [
    {
      title: "Set up Semgrep on your projects",
      description: "Scan new commits for issues automatically.",
    },
    {
      title: "See findings across your organization",
      description: "See the issues Semgrep found across all your repositories.",
    },
    {
      title: "Configure policies",
      description: "Fine-tune what rules should block merges or alert you.",
    },
    {
      title: "Set notification channels",
      description: "Get alerts via Slack or email.",
    },
  ],
};

interface Props {
  step: AuthStep;
}

const List = styled.ul`
  font-size: var(--global-font-size-s);
  list-style: none;
  padding: 0 1em;

  ${(props: CollapsingListProps) =>
    props.collapsed &&
    css`
      overflow-y: hidden;
      max-height: 10rem;
      mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
    `}

  li:not(:first-child) {
    margin-top: var(--global-spacing-m);
  }
`;

interface CollapsingListProps {
  collapsed?: boolean;
}

const ACT_ON_YOUR_BEHALF_EXPLANATION_TEXT = (
  <span>
    This line is not a permission. GitHub adds it on every app permission
    prompt. Accepting on this screen does not let Semgrep take any action on
    your behalf other than those listed on this page. For more details, see{" "}
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://github.community/t/enable-you-to-trigger-actions-in-github/117938/8"
    >
      this GitHub community discussion
    </a>
    .
  </span>
);
const ACT_ON_YOUR_BEHALF_INFO_STICKER = (
  <Sticker>
    <h4 style={{ fontSize: "var(--global-font-size-s)", lineHeight: 1.5 }}>
      <FontAwesomeIcon fixedWidth icon={faQuestionCircle} /> What about the "Act
      on your behalf" line?
    </h4>
    <p style={{ fontSize: "var(--global-font-size-s)", lineHeight: 1.2 }}>
      {ACT_ON_YOUR_BEHALF_EXPLANATION_TEXT}
    </p>
  </Sticker>
);

const NO_WRITE_ONLY_PERMISSIONS_INFO_STICKER = (
  <Sticker>
    <h4 style={{ fontSize: "var(--global-font-size-s)", lineHeight: 1.5 }}>
      <FontAwesomeIcon fixedWidth icon={faQuestionCircle} /> Reading PRs, a
      single file, and workflows
    </h4>
    <p style={{ fontSize: "var(--global-font-size-s)", lineHeight: 1.2 }}>
      Semgrep only uses write permissions on these resources, but those can only
      be requested along with read permissions. Semgrep never accesses your pull
      request data.
    </p>
  </Sticker>
);

export const PermissionsList: React.FC<Props> = ({ step }) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  return (
    <div>
      <h2 style={{ fontSize: "var(--global-font-size-l)" }}>
        <FontAwesomeIcon fixedWidth icon={faUserShield} /> Semgrep asks for{" "}
        {PERMISSIONS[step].length === 1
          ? "this permission"
          : `${PERMISSIONS[step].length} permissions`}
        :
      </h2>
      <List collapsed={collapsed}>
        {PERMISSIONS[step].map((item, key) => (
          <li key={key}>
            <div style={{ color: "var(--global-dark-gray)" }}>{item.title}</div>
            <div style={{ color: "var(--global-mid-gray)" }}>
              {item.description}
            </div>
          </li>
        ))}
        {step === "login" && ACT_ON_YOUR_BEHALF_INFO_STICKER}
        {step === "install" && NO_WRITE_ONLY_PERMISSIONS_INFO_STICKER}
      </List>
      {collapsed && (
        <Button
          minimal
          outlined
          onClick={() => setCollapsed(false)}
          style={{
            display: "block",
            margin: "var(--global-spacing-s) auto var(--global-spacing-l)",
          }}
        >
          Show more
        </Button>
      )}
    </div>
  );
};

export const FeaturesList: React.FC<Props> = ({ step }) => {
  return (
    <div>
      <h2 style={{ fontSize: "var(--global-font-size-l)" }}>
        <FontAwesomeIcon fixedWidth icon={faCheckCircle} /> After{" "}
        {step === "login" ? "logging in" : "installation"}, you'll be able to:
      </h2>
      <List>
        {FEATURES[step].map((item, key) => (
          <li key={key}>
            <div style={{ color: "var(--global-dark-gray)" }}>{item.title}</div>
            <div style={{ color: "var(--global-mid-gray)" }}>
              {item.description}
            </div>
          </li>
        ))}
      </List>
    </div>
  );
};
